import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff, LockOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import apiClient from '../../services/api-client';
import styles from './ResetPasswordPage.module.css';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Clear any existing auth token to prevent redirect loops
  useEffect(() => {
    localStorage.removeItem('token');
  }, []);

  const validateForm = () => {
    if (password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return false;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Token de redefinição de senha inválido');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/auth/reset-password', {
        token,
        new_password: password,
      });

      setSuccess(true);

      // Clear any stale token to prevent issues
      localStorage.removeItem('token');

      // Increase timeout to give user time to see the success message
      // and ensure clean navigation to login page
      setTimeout(() => {
        // Use direct window.location for a full page reload
        // This ensures a clean state when reaching login page
        window.location.href = '/login';
      }, 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ocorreu um erro. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.container}>
        <main className={styles.main}>
          <div className={styles.formContainer}>
            <Paper className={styles.formPaper}>
              <Alert severity="error">Link de redefinição de senha inválido ou expirado.</Alert>
              <Box textAlign="center" mt={2}>
                <Link
                  href="#"
                  variant="body2"
                  onClick={e => {
                    e.preventDefault();
                    navigate('/forgot-password');
                  }}
                  className={styles.link}
                >
                  Solicitar novo link
                </Link>
              </Box>
            </Paper>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        <div className={styles.formContainer}>
          <Paper className={styles.formPaper}>
            <form onSubmit={handleSubmit} noValidate>
              <div className={styles.formHeader}>
                <Typography variant="h6" component="h1">
                  Redefinir Senha
                </Typography>
                <Typography variant="body2">Digite sua nova senha abaixo.</Typography>
              </div>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Senha alterada com sucesso! Você será redirecionado para a página de login em 5
                  segundos...
                </Alert>
              ) : (
                <div className={styles.formFields}>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite sua nova senha"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined sx={{ color: 'var(--acer-green)' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? (
                              <VisibilityOff sx={{ color: 'var(--acer-green)' }} />
                            ) : (
                              <Visibility sx={{ color: 'var(--acer-green)' }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: { height: '45px' },
                      inputProps: { style: { fontSize: '15px' } },
                    }}
                  />

                  <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirme sua nova senha"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined sx={{ color: 'var(--acer-green)' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff sx={{ color: 'var(--acer-green)' }} />
                            ) : (
                              <Visibility sx={{ color: 'var(--acer-green)' }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: { height: '45px' },
                      inputProps: { style: { fontSize: '15px' } },
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    className={styles.submitButton}
                  >
                    {loading ? 'Alterando...' : 'Alterar Senha'}
                  </Button>

                  <Box textAlign="center" mt={2}>
                    <Link
                      href="#"
                      variant="body2"
                      onClick={e => {
                        e.preventDefault();
                        navigate('/login');
                      }}
                      className={styles.link}
                    >
                      Voltar para o Login
                    </Link>
                  </Box>
                </div>
              )}
            </form>
          </Paper>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
